import {
  payrollAddOnSchema,
  payrollAdvanceSchema,
  payrollCycleSchema,
  payrollRunSchema,
  type PayrollAddOn,
  type PayrollAdvance,
  type PayrollCycle,
  type PayrollRun,
  type RecordPayrollAddOnInput,
  type RecordPayrollAdvanceInput,
  type UpsertPayrollCycleInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';
import { isApiRequestError } from '../api/errors.js';

/**
 * Every payroll call the app makes: the shop's cycle, the run that works it out, and the two
 * ledgers that move against it.
 *
 * A sibling of `payouts-api.ts` and shaped exactly like it — one function per call, every
 * response parsed against the shared schema rather than cast. On a screen that decides what
 * cash to hand over, a server that broke the contract must fail loudly rather than render a
 * missing figure as blank.
 *
 * The two write bodies deliberately drop the fields the SERVER owns. `staffUserId` is in the
 * path, and an add-on's `payrollCycleId` is stamped from the shop's own active configuration —
 * it is provenance, not something a client gets to assert — so neither is ours to send.
 */

const PAYROLL = '/payroll';

/** What the shop pays on, over what window, counted from when. */
export async function getPayrollCycle(client: ApiClient): Promise<PayrollCycle> {
  return await client.get(`${PAYROLL}/cycle`, { schema: payrollCycleSchema });
}

/** One configuration per shop, so this writes rather than creates. */
export async function upsertPayrollCycle(
  client: ApiClient,
  input: UpsertPayrollCycleInput,
): Promise<PayrollCycle> {
  return await client.put(`${PAYROLL}/cycle`, input, { schema: payrollCycleSchema });
}

export interface PayrollRunParams {
  /** Any instant inside the wanted window. Omitted for the cycle the shop is in right now. */
  on?: string;
}

/** Everybody's line for the cycle window containing `on`. */
export async function getPayrollRun(
  client: ApiClient,
  params: PayrollRunParams = {},
): Promise<PayrollRun> {
  return await client.get(`${PAYROLL}/run`, {
    schema: payrollRunSchema,
    query: params.on ? { on: params.on } : {},
  });
}

/** The path carries the person, so the body never does. */
export type RecordPayrollAdvanceBody = Omit<RecordPayrollAdvanceInput, 'staffUserId'>;

export async function recordPayrollAdvance(
  client: ApiClient,
  staffUserId: string,
  input: RecordPayrollAdvanceBody,
): Promise<PayrollAdvance> {
  return await client.post(`${PAYROLL}/staff/${staffUserId}/advances`, input, {
    schema: payrollAdvanceSchema,
  });
}

/** The cycle an add-on lands in is the server's to decide, so it is not in this body either. */
export type RecordPayrollAddOnBody = Omit<
  RecordPayrollAddOnInput,
  'staffUserId' | 'payrollCycleId'
>;

export async function recordPayrollAddOn(
  client: ApiClient,
  staffUserId: string,
  input: RecordPayrollAddOnBody,
): Promise<PayrollAddOn> {
  return await client.post(`${PAYROLL}/staff/${staffUserId}/add-ons`, input, {
    schema: payrollAddOnSchema,
  });
}

/**
 * The cycles that have already closed, newest first.
 *
 * There is no "list the past cycles" endpoint and there should not be: a cycle is a window, not
 * a stored record, and the run endpoint already answers "the window containing this instant".
 * So the history is walked — one step back from the start of the window in hand, then one step
 * back from the start of that one — and the walk ends where the server says it must, when a
 * date falls before the cycle's own start date and there is no period for it (a 409).
 */
export async function getPastPayrollRuns(
  client: ApiClient,
  currentPeriodFrom: string,
  limit: number,
): Promise<PayrollRun[]> {
  const runs: PayrollRun[] = [];
  let cursor = currentPeriodFrom;

  while (runs.length < limit) {
    let run: PayrollRun;
    try {
      run = await getPayrollRun(client, { on: instantBefore(cursor) });
    } catch (error) {
      if (isApiRequestError(error) && error.status === 409) break;
      throw error;
    }
    runs.push(run);
    // A window that does not move is a window that would be walked forever.
    if (!isBefore(run.periodFrom, cursor)) break;
    cursor = run.periodFrom;
  }

  return runs;
}

/** The last instant of the period that closed before `isoInstant` began. */
function instantBefore(isoInstant: string): string {
  return new Date(Date.parse(isoInstant) - 1).toISOString();
}

function isBefore(isoInstant: string, other: string): boolean {
  return Date.parse(isoInstant) < Date.parse(other);
}

/**
 * Whether a failure means "this shop has no payroll set up" rather than "something went wrong".
 *
 * Two answers say it, and both must land on the same empty state: the cycle read 404s because
 * nothing has been configured, and the run is refused by the gate with the reason naming an
 * unconfigured or switched-off cycle. Neither is an error a shop can act on by retrying, so
 * neither is allowed to surface as a toast or a red banner.
 */
export function isPayrollCycleUnconfigured(error: unknown): boolean {
  if (!isApiRequestError(error)) return false;
  if (error.status === 404) return true;
  if (error.status !== 409) return false;
  const reason = error.details?.['reason'];
  return reason === 'payroll_cycle_not_configured' || reason === 'payroll_cycle_inactive';
}
